import React from 'react';
import QueryErrorBoundary from '../../../components/error-handling';
import TitleUpdater from '../../../components/common/TitleUpdater';
import { Button, Footer } from '../../../components';
import { useParams } from 'react-router';
import { useQuery } from '@apollo/client';
import { GET_LOCATION_NAME } from '../../../graphql/utils/queries';
import { Location } from '../../exposure/dependency/DependencyMatrix';
import { CyberModuleCard } from '../components';
import HostingEntitiesMitigation from '../../../components/assets/HostingEntitiesMitigation';
import ApplicationWorkarounds from '../../../components/assets/ApplicationWorkarounds';
import HostingEntitiesMapping from '../../../components/assets/HostingEntitiesMapping';
import ApplicationDependencyMapping from '../../../components/assets/ApplicationDependencyMapping';
import PageHeader from '../../../components/PageHeader';
import { CheckOutlined } from '@ant-design/icons';
import { useFetchLocationById, useMarkLocationAsUpdated } from './hooks';
import { formatDate } from '../../../utils/formatDate';
import { message } from 'antd';

type FetchedLocation = {
  findLocationByUuid: Location;
};

const LOCATION_LEVEL_CARDS_MOCKED = [
  {
    icon: <ApplicationDependencyMapping />,
    title: 'Application Dependency Mapping',
    description:
      'Use this to determine how important your application are to the business',
    progress: '60 applications mapped',
    link: (locationId: string) =>
      `/cyber/location-level/${locationId}/application-mapping`,
    isActive: true,
  },
  {
    icon: <ApplicationWorkarounds />,
    title: 'Application Workarounds',
    description:
      'Use this section to capture any manual workarounds that are in place to reduce the impact of disruptions caused by applications',
    progress: '9 of 60 applications with workarounds',
    link: (locationId: string) =>
      `/cyber/location-level/${locationId}/application-workarounds`,
    isActive: true,
  },
  {
    icon: <HostingEntitiesMapping />,
    title: 'Hosting Entities Mapping',
    description:
      'Use this section to capture where applications are hosted in order to capture and quantify related exposures',
    progress: '30 of 60 applications mapped to a hosting entity',
    link: (locationId: string) => `#`,
    isActive: false,
  },
  {
    icon: <HostingEntitiesMitigation />,
    title: 'Hosting Entities Mitigation',
    description:
      'Use this section to capture where applications are hosted in order to capture and quantify related exposures',
    progress: '3 of 5 hosting entities have mitigation',
    link: (locationId: string) => `#`,
    isActive: false,
  },
];

const LocationLevelOptions = () => {
  const { locationId } = useParams();
  const { handleMarkAsUpdated, loading: markAsUpdatedLoading } =
    useMarkLocationAsUpdated();

  const { data, error } = useQuery<FetchedLocation>(GET_LOCATION_NAME, {
    variables: {
      uuid: locationId,
    },
  });

  const { data: location, refetch } = useFetchLocationById(locationId ?? '');

  const locationName = data?.findLocationByUuid.locationName ?? '';

  const handleMarkAsUpdatedClick = () => {
    if (locationId) {
      handleMarkAsUpdated(locationId, () => {
        message.success('Location changes saved!');
        refetch();
      });
    }
  };

  return (
    <>
      <TitleUpdater title={`Location Level Applications`} />
      <QueryErrorBoundary error={error}>
        <PageHeader
          title="Location Level Applications"
          subtitle={`${locationName} Overview`}
          description={`This section is all about capturing the importance and workarounds for applications and hosting entities at the ${locationName} location. Starting with Application Dependency Mapping will allow you to build a comprehensive view of what matters most.`}
          statusText={`Last updated: ${
            location?.lastManuallyUpdatedAt
              ? formatDate(location?.lastManuallyUpdatedAt)
              : 'Never'
          }`}
          actionButton={
            <Button
              type="secondary"
              icon={<CheckOutlined />}
              className="w-full text-sm font-bold max-w-[188px] py-2"
              onClick={handleMarkAsUpdatedClick}
              disabled={markAsUpdatedLoading}
            >
              {markAsUpdatedLoading ? 'Updating...' : 'Mark as Updated'}
            </Button>
          }
        />
        <div className="grid gap-4">
          {LOCATION_LEVEL_CARDS_MOCKED.map((section) => (
            <CyberModuleCard
              key={section.title}
              {...section}
              link={locationId ? section.link(locationId) : '#'}
              isActive={section.isActive}
            />
          ))}
        </div>
        <Footer backPath={`/cyber/location-level/list`} />
      </QueryErrorBoundary>
    </>
  );
};

export default LocationLevelOptions;
